import React, { useEffect, useState } from "react";
import { api, extractApiError } from "@/lib/api";
import { apiErrorCode, apiErrorFailed, apiErrorOwed } from "../lib/apiErrors";
import { formatMoneyLedger } from "../lib/formatMoney";
import { useT, useLanguage } from "@/lib/LanguageContext";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { Loader2, Trash2, Search } from "lucide-react";
import LoadFailure from "../components/ui/LoadFailure";
import Pager from "../components/ui/Pager";
import { TenantTableRow } from "./tenants/TenantTableRow";
import { TENANT_COLUMNS, tenantTableWidthRem } from "./tenants/tenantColumns";
import { EntitlementsEditorModal } from "./tenants/EntitlementsEditorModal";
import { PlanEditorModal } from "./tenants/PlanEditorModal";
import { SEARCH_MAX_LENGTH, useDebouncedSearch } from "./useDebouncedSearch";
import { usePagedList } from "./usePagedList";

const PAGE_SIZE = 25;
// Kept in step with TENANT_STATUSES in super_admin_routes.py: the server refuses anything
// else (422), so the filter can only offer these.
const STATUSES = ["pending", "active", "past_due", "suspended", "cancelled", "churned"];

export default function TenantsList() {
  const t = useT("superadmin");
  const tc = useT("common");
  const { lang } = useLanguage();
  const [plans, setPlans] = useState([]);
  const [page, setPage] = useState(1);
  const [status, setStatus] = useState("");
  const [search, setSearch, q] = useDebouncedSearch(() => setPage(1));
  // `reload` re-reads the SAME page and filters (after a mutation); `retry` repeats a failed read.
  const { data: result, totalForCurrent, loading, loadFailed, reload, retry } = usePagedList({
    path: "/super-admin/tenants", filters: { q, status }, page, setPage, pageSize: PAGE_SIZE,
  }); // result: { total, items }
  const [entitlementsFor, setEntitlementsFor] = useState(null);
  const [planEditor, setPlanEditor] = useState(null);
  const [purging, setPurging] = useState(false);

  useEffect(() => {
    // Not /saas/plans (public, active only): a tenant on a RETIRED plan must still show up
    // as a selectable target in PlanEditorModal.
    api.get("/super-admin/plans").then((p) => setPlans(p.data)).catch(() => {});
  }, []);

  const changeStatus = (value) => { setStatus(value); setPage(1); };

  // A practice Solunary still owes money (D7 contract 3.14): every amount per currency, then the
  // payouts still on their way, then the client payments not settled yet. The wallet includes
  // the money those payouts carry until they are paid, so all can be named at once. With
  // nothing in the wallet, the first thing still in flight leads the refusal. The amounts are in
  // the ledger style (ISO code, never "$"): the refusal can name several currencies at once.
  const owedRefusal = (e) => {
    const { owed, payouts, clientPayments } = apiErrorOwed(e);
    const sentences = [];
    if (owed.length) {
      const amounts = owed.map((m) => formatMoneyLedger(m.cents, m.currency, lang)).join(", ");
      sentences.push(t("tl.deleteOwed", { amounts }));
      if (payouts.length) sentences.push(t("tl.deleteOwedPayouts", { n: payouts.length }));
    } else if (payouts.length) {
      sentences.push(t("tl.deletePayoutsInFlight", { n: payouts.length }));
    }
    if (clientPayments.length) {
      sentences.push(sentences.length
        ? t("tl.deleteClientPayments", { n: clientPayments.length })
        : t("tl.deleteClientPaymentsInFlight", { n: clientPayments.length }));
    }
    // A refusal naming nothing (a malformed body) still reads as a failure, never as blank.
    return sentences.join(" ") || t("tl.error");
  };

  // The refusals of a practice deletion (D7 contract 3.5, 3.14), worded here in the UI language:
  // the server's English message is never shown. `null`: not one of them.
  const deleteRefusal = (e) => {
    const code = apiErrorCode(e);
    if (code === "PRACTICE_DELETE_STRIPE_FAILED") return t("tl.deleteStripeFailed", { n: apiErrorFailed(e).length });
    if (code === "PRACTICE_DELETE_IN_PROGRESS") return t("tl.deleteInProgress");
    if (code === "PRACTICE_DELETE_OWED") return owedRefusal(e);
    return null;
  };

  // The refusals of abandoning an unfinished deletion (D7 contract 3.10), same rule.
  const abandonRefusal = (e) => {
    const code = apiErrorCode(e);
    if (code === "PRACTICE_DELETION_ABANDON_STRIPE_FAILED") return t("tl.abandonStripeFailed", { n: apiErrorFailed(e).length });
    if (code === "PRACTICE_DELETE_IN_PROGRESS") return t("tl.deleteInProgress");
    if (code === "PRACTICE_DELETION_NOT_PENDING") return t("tl.abandonNotPending");
    if (code === "PRACTICE_DELETION_COMMITTED") return t("tl.abandonCommitted");
    return null;
  };

  const act = async (tid, action) => {
    try {
      let done = "tl.opOk";
      if (action === "delete") {
        if (!window.confirm(t("tl.confirmDelete", { tid }))) return;
        await api.delete(`/super-admin/tenants/${tid}`);
      } else if (action === "abandonDeletion") {
        if (!window.confirm(t("tl.abandonConfirm", { tid }))) return;
        await api.post(`/super-admin/tenants/${tid}/deletion/abandon`);
        done = "tl.abandonSuccess";
      } else {
        await api.post(`/super-admin/tenants/${tid}/${action}`);
      }
      toast.success(t(done));
      reload();
    } catch (e) {
      const refusal = action === "delete" ? deleteRefusal(e)
        : action === "abandonDeletion" ? abandonRefusal(e) : null;
      toast.error(refusal ?? extractApiError(e, t("tl.error"), t));
      // A refused deletion (or abandon) leaves, or changes, a durable record on the row
      // (badge, "Retry deletion"): read it back so the row shows it without a manual refresh.
      if (refusal !== null) reload();
    }
  };

  const purgeTestTenants = async () => {
    try {
      const { data } = await api.get("/super-admin/test-tenants");
      if (!data.test_count) {
        toast.info(t("tl.purgeNone"));
        return;
      }
      const sample = data.tenants.slice(0, 8).map((x) => x.slug).join(", ");
      if (!window.confirm(
        t("tl.purgeConfirm", { count: data.test_count, preserved: data.preserved.join(", "), sample })
      )) return;
      setPurging(true);
      const res = await api.post("/super-admin/purge-test-tenants");
      toast.success(t("tl.purgeSuccess", { n: res.data.deleted_tenants }));
      // Practices left whole (D7 contract 3.5): those Solunary still owes money are named apart
      // (3.14: pay them out first), the others were refused by Stripe or a running deletion.
      const blocked = Array.isArray(res.data.blocked) ? res.data.blocked : [];
      const owed = blocked.filter((b) => b.reason === "owed").length;
      const others = blocked.length - owed;
      if (others > 0) toast.warning(t("tl.purgeBlocked", { n: others }));
      if (owed > 0) toast.warning(t("tl.purgeBlockedOwed", { n: owed }));
      reload();
    } catch (e) {
      toast.error(extractApiError(e, t("tl.purgeError"), t));
    } finally {
      setPurging(false);
    }
  };

  return (
    <div data-testid="sa-tenants">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-8">
        <div>
          <h1 className="font-sol font-semibold text-3xl sm:text-4xl">{t("tl.title")}</h1>
          <p className="text-slate-500 text-sm mt-1" data-testid="sa-tenants-count">
            {/* Under a search or status filter the total is the matches, not the platform. */}
            {/* No total for the filters on screen (pending, failed): no number rather than a wrong one. */}
            {totalForCurrent === null
              ? t("tl.countLineBare")
              : t(q || status ? "tl.countLineFiltered" : "tl.countLine", { n: totalForCurrent })}
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-3">
          <div className="relative flex-1 sm:flex-initial">
            <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
            <input
              data-testid="sa-tenants-search"
              maxLength={SEARCH_MAX_LENGTH}
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder={t("tl.searchPh")}
              className="bg-white shadow-sm border border-slate-200 rounded-full pl-10 pr-4 py-2 text-sm text-slate-800 placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-amber-300/40 w-full sm:w-64"
            />
          </div>
          <select
            value={status}
            onChange={(e) => changeStatus(e.target.value)}
            className="bg-white shadow-sm border border-slate-200 rounded-full px-4 py-2 text-sm text-slate-700"
            aria-label={t("tl.colStatus")}
            data-testid="sa-tenants-status"
          >
            <option value="">{t("tl.allStatuses")}</option>
            {STATUSES.map((v) => <option key={v} value={v}>{t(`tl.status.${v}`)}</option>)}
          </select>
          <Button
            variant="outline"
            size="sm"
            onClick={purgeTestTenants}
            disabled={purging}
            data-testid="sa-purge-test-btn"
            className="border-rose-200 text-rose-600 hover:bg-rose-50"
          >
            {purging ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Trash2 className="w-4 h-4 mr-2" />}
            {t("tl.purgeBtn")}
          </Button>
        </div>
      </div>

      <LoadFailure
        message={loadFailed && !loading ? tc("errors.loadFailed") : ""}
        onRetry={retry}
        retryLabel={t("sal.retry")}
        testId="sa-tenants-load-error"
        retryTestId="sa-tenants-load-error-retry"
      />

      {loading && (loadFailed || !result) && <Loader2 className="w-6 h-6 animate-spin text-amber-600" />}

      {!loadFailed && result && (
        <div
          className={`rounded-2xl border border-slate-200 overflow-hidden transition-opacity ${loading ? "opacity-60" : ""}`}
          aria-busy={loading}
          data-testid="sa-tenants-table"
        >
          {/* Fixed layout sized by a colgroup (tenants/tenantColumns.js): every column, the row
              actions included, fits the card at 1280px. Fixed columns carry a
              width; the flexible ones (name, owner) carry none and share the remainder. `minWidth`
              is fixed widths + flexible minimums: below it the wrapper scrolls (a narrow
              viewport) rather than crushing columns. */}
          <div className="overflow-x-auto">
            <table className="w-full table-fixed text-sm" style={{ minWidth: `${tenantTableWidthRem()}rem` }}>
              <colgroup>
                {TENANT_COLUMNS.map((c) => <col key={c.id} style={c.flex ? undefined : { width: `${c.width}rem` }} />)}
              </colgroup>
              <thead className="bg-slate-50">
                <tr className="text-left text-slate-400 text-xs uppercase tracking-wider">
                  {TENANT_COLUMNS.map((c) => (
                    <th key={c.id} scope="col" className="px-3 py-4 truncate" title={t(c.headerKey)}>{t(c.headerKey)}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {result.items.map((tn) => (
                  <TenantTableRow
                    key={tn.tenant_id}
                    tn={tn}
                    t={t}
                    lang={lang}
                    onPlanEdit={setPlanEditor}
                    onEntitlementsEdit={setEntitlementsFor}
                    onAct={act}
                  />
                ))}
                {result.items.length === 0 && (
                  <tr>
                    <td colSpan={TENANT_COLUMNS.length} className="px-6 py-10 text-center text-slate-400" data-testid="sa-tenants-empty">{t("tl.empty")}</td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
          {result.total > PAGE_SIZE && (
            <Pager
              page={page}
              pageSize={PAGE_SIZE}
              total={result.total}
              shown={result.items.length}
              onPage={setPage}
              infoText={(v) => t("sal.pagerInfo", v)}
              prevLabel={t("sal.pagerPrev")}
              nextLabel={t("sal.pagerNext")}
              testId="sa-tenants-pager"
              tone="slate"
            />
          )}
        </div>
      )}

      <EntitlementsEditorModal
        // Keyed by tenant: opening another practice starts from ITS map, never from the
        // previous one's draft.
        key={entitlementsFor?.tenant_id || "none"}
        tenant={entitlementsFor}
        onClose={() => setEntitlementsFor(null)}
        onSaved={reload}
        t={t}
      />
      <PlanEditorModal
        editor={planEditor}
        setEditor={setPlanEditor}
        plans={plans}
        reload={reload}
        t={t}
      />
    </div>
  );
}
